import { createRootRoute, createRoute, createRouter } from '@tanstack/react-router';

import { ErrorPage } from './routes/ErrorPage';
import { LoginPage } from './routes/LoginPage';
import { Root } from './routes/Root';
import { SecurityPage } from './routes/SecurityPage';

const rootRoute = createRootRoute({
  component: Root,
  // Any unknown path inside this SPA is a 404 page, not a blank screen.
  notFoundComponent: () => <ErrorPage kind="notFound" />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  // A failed provider sign-in redirects here with /?error=<code>. Any string
  // is accepted; LoginPage maps an unknown code to the generic message.
  validateSearch: (search: Record<string, unknown>): { error?: string } =>
    typeof search.error === 'string' && search.error !== '' ? { error: search.error } : {},
  component: function LoginRouteComponent() {
    const { error } = loginRoute.useSearch();
    return <LoginPage ssoError={error} />;
  },
});

const securityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/security',
  component: SecurityPage,
});

/**
 * The error routes are addressable so Traefik, the console SPA and the API can
 * redirect a browser to a specific explanation.
 */
const sessionExpiredRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/session-expired',
  component: () => <ErrorPage kind="session" />,
});

const forbiddenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forbidden',
  component: () => <ErrorPage kind="forbidden" />,
});

const serverErrorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/error',
  component: () => <ErrorPage kind="server" />,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  securityRoute,
  sessionExpiredRoute,
  forbiddenRoute,
  serverErrorRoute,
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
